/**
 * Client-side mirror of the server's avatar admission rules — Phase 2.5b.
 *
 * ===========================================================================
 * THE SERVER IS THE AUTHORITY. THIS FILE IS A COURTESY.
 * ===========================================================================
 * `avatars.service.ts` decides admission on MAGIC BYTES, caps the stream at
 * 5MB inside multer, and re-encodes everything it accepts. Nothing here changes
 * that — a crafted file that lies about its `File.type` sails past this mirror
 * and is refused by the sniff on the server, which is the design. What the
 * mirror buys is the ORDER of the refusal: an 8MB photo fails here in
 * microseconds instead of after 5MB of upload, and a `.heic` pick is told why
 * before any request exists.
 *
 * The constants restate the server's (`AVATAR_MAX_BYTES`, the jpeg/png/webp
 * allowlist in `image-sniff.ts`) rather than importing them: `@crf/shared`
 * type-only discipline (see `lib/permissions.ts`) keeps runtime values out of
 * the bundle, and the API package is not importable from here at all. The
 * numbers are pinned by `avatar-file.test.ts` so a drift is a red test, not a
 * silent disagreement.
 */

/** 5MB — the same cap multer enforces server-side while the body streams. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/** The server's admission allowlist, as browser MIME types. */
export const AVATAR_ACCEPT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** For the file input's `accept` attribute — narrows the OS picker's default view. */
export const AVATAR_ACCEPT_ATTR = AVATAR_ACCEPT_TYPES.join(',');

/**
 * One error sentence, or `null` for a file worth uploading.
 *
 * Copy discipline: the sentence says what to DO (choose a different file), not
 * what the validator computed. The two cases stay two different sentences —
 * "wrong kind" and "too big" have different fixes.
 */
export function validateAvatarFile(file: Pick<File, 'type' | 'size'>): string | null {
  if (!(AVATAR_ACCEPT_TYPES as readonly string[]).includes(file.type)) {
    return 'Choose a JPEG, PNG or WebP image.';
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return 'Choose an image under 5 MB.';
  }
  return null;
}
